// SPDX-License-Identifier: Apache-2.0

// Tiny en/tr locale dict for VaultExperience. Keys mirror the originals from
// `~/Documents/GitHub/tether/apps/web/src/i18n/` so a future merge is a
// straight key move, not a rename.
//
// Default locale is `en`. DavaKasası locks `tr` via `forceLocale` on the
// VaultExperience component (so a Turkish lawyer never reads English chrome by
// accident).

export type Locale = "en" | "tr";

let CURRENT: Locale = "en";

export function setLocale(loc: Locale): void {
  CURRENT = loc;
}

export function getLocale(): Locale {
  return CURRENT;
}

type Dict = Record<string, string>;
type Bundle = { en: Dict; tr: Dict };

const STRINGS: Bundle = {
  en: {
    // empty state
    empty_solo: "Your vault is empty.",
    empty_room: "This room is empty.",
    empty_press_prefix: "Press",
    empty_new_chip: "+ new",
    empty_to_begin: "or drop files anywhere to begin.",
    drop_anywhere: "Drop notes anywhere on this page",
    drop_or_pick: "or pick a folder",
    pick_folder: "Pick a folder",
    try_demo: "Try with demo data",
    demo_loaded_one: "Loaded 1 demo note",
    demo_loaded_many: "Loaded {n} demo notes",
    demo_already_present: "Demo already loaded",

    // hints
    hint_click_panel: "click any panel to read · ",
    hint_peers_online_one: "1 peer online",
    hint_peers_online_many: "{n} peers online",

    // dropzone
    dropzone_heading: "Drop to add to your vault",
    dropzone_formats: ".md · .txt accepted (docx/pdf/csv coming)",
    dropzone_assurance: "Your files stay on this device. No upload.",

    // toasts
    toast_added_one: "Added 1 note",
    toast_added_many: "Added {n} notes",
    toast_skipped_one: "skipped 1",
    toast_skipped_many: "skipped {n}",
    toast_none_supported: "Nothing supported in that drop",
    toast_unsupported: "Unsupported file type — try .md or .txt",
    toast_ingesting: "Reading files…",

    // search
    search_placeholder: "search the vault…",

    // topbar
    topbar_title: "tether",
    topbar_count_one: "1 note",
    topbar_count_many: "{n} notes",
    topbar_new: "+ new",
    topbar_pick: "open folder",
  },
  tr: {
    empty_solo: "Kasa boş.",
    empty_room: "Bu oda boş.",
    empty_press_prefix: "Bas",
    empty_new_chip: "+ yeni",
    empty_to_begin: "ya da dosyaları sayfaya bırak.",
    drop_anywhere: "Notları buraya bırak",
    drop_or_pick: "ya da bir klasör seç",
    pick_folder: "Klasör seç",
    try_demo: "Demo veriyle dene",
    demo_loaded_one: "1 demo not yüklendi",
    demo_loaded_many: "{n} demo not yüklendi",
    demo_already_present: "Demo zaten yüklü",

    hint_click_panel: "okumak için panele tıkla · ",
    hint_peers_online_one: "1 kişi online",
    hint_peers_online_many: "{n} kişi online",

    dropzone_heading: "Kasaya eklemek için bırak",
    dropzone_formats: ".md · .txt destekleniyor (docx/pdf/csv yolda)",
    dropzone_assurance: "Dosyalar bu cihazdan çıkmaz. Yükleme yok.",

    toast_added_one: "1 not eklendi",
    toast_added_many: "{n} not eklendi",
    toast_skipped_one: "1 atlandı",
    toast_skipped_many: "{n} atlandı",
    toast_none_supported: "Bırakılanların hiçbiri desteklenmiyor",
    toast_unsupported: "Desteklenmeyen tür — .md ya da .txt deneyin",
    toast_ingesting: "Okunuyor…",

    search_placeholder: "kasada ara…",

    topbar_title: "tether",
    topbar_count_one: "1 belge",
    topbar_count_many: "{n} belge",
    topbar_new: "+ yeni",
    topbar_pick: "klasör aç",
  },
};

/** Lookup with `{n}` interpolation. Falls back to `en`, then to the key. */
export function t(key: string, vars?: Record<string, string | number>): string {
  const dict = STRINGS[CURRENT] ?? STRINGS.en;
  let s = dict[key] ?? STRINGS.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return s;
}
